import { useRouter } from 'next/navigation';
import { CartItemViewModel, ProductListViewModel } from '@/types/api';
import { sendRecommendedProductCtaClickEvent } from '@/features/analytics/datalayer/datalayer';
import { RecommendedProductCtaClickParams } from '@/features/analytics/datalayer/types';
import { Button } from '@/features/button';
import { NavigationCarousel } from '@/features/carousel';
import { useMobileDetect } from '@/features/mobile-detect-context/mobile-detect-provider';
import { Modal } from '@/features/modal';
import { PDPCard } from '@/features/product-card';
import { generatePDPPageCarouselItemsProps } from '@/features/product-card/utils/generate-pdp-card-props';
import { useCartReturnUrl } from '@/app/(order)/cart/_utils/use-cart-return-url';
import { OrderPagesURL } from '@/app/(order)/types';
import useUser from '@/utils/use-user';
import { useGetCartAlsoBought } from '../_utils/use-get-cart-also-bought';
interface Props {
  isOpen: boolean;
  handleClose: () => void;
  productInCart: CartItemViewModel | null;
}
const AddToCartModal = ({
  isOpen,
  handleClose,
  productInCart
}: Props) => {
  const {
    isMobile
  } = useMobileDetect();
  const router = useRouter();
  const {
    data,
    isLoading: isLoadingProducts
  } = useGetCartAlsoBought(isOpen);
  const {
    setCartBackReturnUrl
  } = useCartReturnUrl();
  const {
    user
  } = useUser();
  const keepShoppingHandler = () => handleClose();
  const goToCartHandler = () => {
    setCartBackReturnUrl();
    router.push(OrderPagesURL.CART);
  };
  if (!productInCart) return null;
  const handleSendRecommendedProductClickEvent = ({
    item,
    index
  }: {
    item: ProductListViewModel;
    index: number;
  }) => {
    const eventData: RecommendedProductCtaClickParams = {
      elementType: 'AddToCart-ProductList',
      elementName: 'AddToCart-ProductList',
      elementIndex: index + 1,
      ImageUrl: item.imageUrl ?? '',
      item_id: item.id ?? '',
      item_name: item?.nameFa ?? '',
      item_brand: item?.brand?.nameFa ?? '',
      price: item.basePrice ?? 0,
      discount: item.discountPrice ?? 0,
      user_id: user?.id ?? '',
      slug: item.slug ?? '',
      item_category: item?.breadcrumb?.find(c => c.level === 1)?.nameFa ?? '',
      item_category2: item?.breadcrumb?.find(c => c.level === 2)?.nameFa ?? '',
      item_category3: item?.breadcrumb?.find(c => c.level === 3)?.nameFa ?? '',
      item_category4: item?.breadcrumb?.find(c => c.level === 4)?.nameFa ?? '',
      item_category5: item?.breadcrumb?.find(c => c.level === 5)?.nameFa ?? ''
    };
    sendRecommendedProductCtaClickEvent(eventData);
  };
  return <Modal isOpen={isOpen} handleClose={handleClose} title='کالا به سبد خرید شما افزوده شد!' className='w-full pt-1 lg:min-w-[508px]' type='success' data-sentry-element="Modal" data-sentry-component="AddToCartModal" data-sentry-source-file="add-to-cart-modal.tsx">
            <div className='mt-6 flex flex-col gap-6'>
                {isLoadingProducts ? <div className='col-span-full flex flex-col gap-2'>
                        <div className='h-5 w-40 animate-pulse rounded-md bg-gray-200' />

                        <div className='flex h-[252px] w-full gap-2 overflow-hidden lg:h-[290px]'>
                            {Array(3).fill(null).map((_, i) => <div key={i} className='h-full min-w-[164px] animate-pulse rounded-md bg-gray-200 lg:min-w-[224px]' />)}
                        </div>
                    </div> : !!data?.length && <div className='col-span-full flex flex-col gap-2'>
                            <span className='block text-body-14 text-text-black lg:text-body-16'>
                                سبد خرید خود را کامل کنید:
                            </span>
                            <NavigationCarousel options={{
          perView: 1.1,
          mode: 'free-snap',
          spacing: 6,
          breakPoints: {
            desktop: {
              perView: 2.1
            },
            mobile: {
              perView: 2.1
            }
          }
        }}>
                                {data.map((item, index) => {
            const data = generatePDPPageCarouselItemsProps(item, isMobile ? 'horizontal' : 'vertical');
            return <div onClick={() => handleSendRecommendedProductClickEvent({
              item,
              index
            })} key={item.id || index} className='keen-slider__slide h-auto w-full'>
                                            <PDPCard {...data} type='vertical' />
                                        </div>;
          })}
                            </NavigationCarousel>
                        </div>}

                <div className='flex gap-4 lg:gap-6'>
                    <Button size={isMobile ? 'medium' : 'large'} graphicStyle='filled' className='w-full' onClick={goToCartHandler} data-sentry-element="Button" data-sentry-source-file="add-to-cart-modal.tsx">
                        مشاهده سبد خرید
                    </Button>
                    <Button size={isMobile ? 'medium' : 'large'} graphicStyle='outlined' className='w-full' onClick={keepShoppingHandler} data-sentry-element="Button" data-sentry-source-file="add-to-cart-modal.tsx">
                        ادامه دادن خرید
                    </Button>
                </div>
            </div>
        </Modal>;
};
export default AddToCartModal;